import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  MenuItem,
  Pagination,
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TableSortLabel,
  Checkbox,
  Typography,
  IconButton,
  FormControl,
  Select,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchBarContainer from "./SearchBarContainer";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material";
import Header from "./Header";
import PaginationSettingsModal from "./SettingsModal";

const useStyles = makeStyles((theme) => ({
  tableCell: {
    backgroundColor: "transparent",
    fontSize: "12px !important",
    // color: `${theme.palette.text.primary} !important`,
    height: "30px",
    // border: `1px solid ${theme.palette.border.color} !important`,
  },
  headerCell: {
    // backgroundColor: theme.palette.primary.light,
    fontSize: "14px",
    // color: theme.palette.primary.contrastText,
    height: "30px",
  },
}));

const RenderColumnItem = (column, row, object,setTableData) => {
  const getValue = () => {
    if (column.render && typeof column.render === "function") {
      return column.render(row);
    }

    const value = row[column.id];
    if (value === null || value === undefined || value === "") {
      return column.default || "N/A";
    }

    return value;
  };

  const displayValue = getValue();

  switch (column.type) {
    case "textField":
      return (
        <Typography
          sx={{
            
            fontSize: "14px !important",
            
          }}
        >
          {displayValue}
        </Typography>
      );
    case "text":
      return (
        <Typography
          sx={{
            
            fontSize: "14px !important",
            
          }}
        >
          {displayValue}
        </Typography>
      );
    case "checkbox":
      return (
        <Checkbox
          color="primary"
          indeterminate={column.indeterminate}
          checked={column.checked}
          onChange={column.onChange}
          sx={{
            color: "#f5f5f5",
            "& .MuiSvgIcon-root": {
              fontSize: "14px !important",
            },
            "&.Mui-checked": { color: "#006bce" },
            "&.MuiCheckbox-indeterminate": { color: "#006bce" },
          }}
        />
      );
    case "toggle":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={row[column.id]}
              onChange={(e) =>
                column.onToggleChange(row.id, e.target.checked)
              }
              color="primary"
            />
          }
          labelPlacement="center"
          sx={{
            marginLeft: 0,
            width: "100%",
            "& .MuiTypography-root": {
              fontSize: "13px",
              color: "white",
              opacity: 0.9,
            },
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4CAF50',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#4CAF50',
            },
          }}
        />
      );
          
    case "icon":
      return (
        <column.icon
          sx={{
            
            fontSize: "14px",
            
          }}
        />
      );
    case "button":
      return (
        <button
          onClick={column.onClick}
          style={{
            backgroundColor: "transparent",
            border: "none",
            fontSize: "14px",
            cursor: "pointer",
            padding: "0",
            margin: "0",
           
          }}
        >
          {column.label}
        </button>
      );
    case "link":
      return (
        <a
          href
          onClick={column.onClick}
          style={{
            
            fontSize: "14px",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {column.label}
        </a>
      );
    case "image":
      return (
        <img
          src={column.src || "/placeholder.svg"}
          alt={column.alt}
          style={{
            width: column.width || "50px",
            height: column.height || "50px",
            objectFit: column.objectFit || "cover",
            borderRadius: column.borderRadius || "4px",
            cursor: column.cursor || "pointer",
            "&:hover": {
              transform: column.transform || "scale(1.05)",
            },
          }}
        />
      );
    case "dropDown":
      const option = column.options?.find((opt) => opt.value === displayValue);
      return (
        <Typography
          sx={{
            
            fontSize: "14px !important",
            
          }}
        >
          {option ? option.label : displayValue}
        </Typography>
      );
    case "date":
      const dateValue = displayValue;
      const isValidDate =
        dateValue && dateValue !== "N/A" && !isNaN(new Date(dateValue));

      return (
        <Typography
          style={{
            
            fontSize: "14px",
          }}
        >
          {isValidDate
            ? new Date(dateValue).toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            : displayValue}
        </Typography>
      );
    case "geoJson":
      const latitude = row[`${object}Latitude`];
      const longitude = row[`${object}Longitude`];
      return (
        <Typography
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            
            textDecoration: "none",
            fontSize: "14px !important",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {latitude && longitude
            ? `${latitude}, ${longitude}`
            : column.default || "N/A"}
        </Typography>
      );
    case "number":
      return (
        <Typography
          sx={{
            
            fontSize: "14px !important",
           
          }}
        >
          {displayValue}
        </Typography>
      );
    case "plan":
      const planValue = displayValue;
      const teamSizeMapping = { free: 3, pro: 10, enterprise: 20 };
      const teamSize =
        teamSizeMapping[planValue?.toLowerCase()] || column.default;

      return (
        <Typography
          sx={{
            
            fontSize: "14px !important",
            
          }}
        >
          {planValue === "N/A"
            ? column.default || "N/A"
            : `${planValue} (${teamSize} users)`}
        </Typography>
      );
    default:
      return (
        <Typography
          sx={{
            
            fontSize: "14px !important",
            
          }}
        >
          {displayValue}
        </Typography>
      );
  }
};

// Sorting utility functions
const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  const aValue = a[orderBy];
  const bValue = b[orderBy];
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const ListComponent = (props) => {
  const {
    header = true,
    Icon,
    objectDefinition,
    title,
    showAddButton,
    addButtonText,
    setShowAddForm,
    showActions,
    actionOptions,
    selectedAction,
    onAddClick,
    onDeleteClick,
    onRefresh,
    object,
    columns,
    data,
    selectedRows,
    setSelectedRows,
    setShowUpdateForm,
    layoutView,
    onLayoutChange,
  } = props;

  const theme = useTheme();
  const rowCount = data?.length;
  const numSelected = selectedRows?.length;
  const classes = useStyles(theme);

  // Pagination settings state
  const [paginationSettings, setPaginationSettings] = useState({
    rowsPerPage: 20,
    showPageInfo: true,
    showFirstLast: true,
    showRowsPerPageSelector: false,
  });

  const [page, setPage] = useState(1);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const totalPages = Math.ceil(data?.length / paginationSettings.rowsPerPage);

  const [tableData, setTableData] = useState(
    data?.slice(
      (page - 1) * paginationSettings.rowsPerPage,
      page * paginationSettings.rowsPerPage
    )
  );

  // Popover state
  const [anchorElOptions, setAnchorElOptions] = useState(null);
  const [anchorElAttributes, setAnchorElAttributes] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElLayout, setAnchorElLayout] = useState(null);
  const openOptions = Boolean(anchorElOptions);
  const openAttributes = Boolean(anchorElAttributes);
  const openFilter = Boolean(anchorElFilter);
  const openLayout = Boolean(anchorElLayout);

  //sort state
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(columns[0]?.id || "");

  // Column visibility state
  const [visibilityStatus, setVisibilityStatus] = useState(
    columns.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {})
  );

  // Check if a row is selected by comparing its ID
  const isSelected = (row, rowIndex, page, rowsPerPage) => {
    const selectedIndex = selectedRows.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    const isSelected = selectedIndex !== -1;
    return isSelected;
  };

  // handle form add state
  const handleAddClick = () => {
    // 
    if (objectDefinition) {
      props?.setFormObject?.(objectDefinition);
    } else {
      props?.setFormObject(null);
      console.warn("objectDefinition is undefined");
    }
    setShowAddForm(true);
  };

  // Handle form modify state
  const handleModifyClick = () => {
    if (setShowUpdateForm && typeof setShowUpdateForm === "function") {
      setShowUpdateForm(true)
    } else {
      console.warn("setShowUpdateForm function not provided")
    }
  }

  // Handle delete click


  // Handle Popover open/close
  const handleOptionsClick = (event) => {
    setAnchorElOptions(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setAnchorElOptions(null);
    setAnchorElAttributes(null);
    setAnchorElLayout(null);
  };

  //handle attributes
  const handleAttributesClick = (event) => {
    setAnchorElAttributes(anchorElOptions);
    setAnchorElOptions(null);
  };

  const handleAttributesClose = () => {
    setAnchorElOptions(anchorElAttributes);
    setAnchorElAttributes(null);
  };

  const handleSettingsClick = () => {
    handleOptionsClose();
  };

  // Handle column visibility toggle
  const handleToggle = (columnId) => (event) => {
    setVisibilityStatus((prev) => ({
      ...prev,
      [columnId]: event.target.checked,
    }));
  };

  //handle filter
  const handleFilterClick = useCallback((event) => {
    setAnchorElFilter(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorElFilter(null);
  }, []);

  // Select all rows
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows([...data]);
      return;
    }
    setSelectedRows([]);
  };

  // Handle layout click
  const handleLayoutClick = (event) => {
    setAnchorElLayout(anchorElOptions);
    setAnchorElOptions(null);
  };

  const handleLayoutClose = () => {
    setAnchorElOptions(anchorElLayout);
    setAnchorElLayout(null);
  };

  // Toggle selection of a single row
  const handleSelected = (event, row, rowIndex) => {
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    const newSelectedRows = [...selectedRows];

    if (selectedIndex === -1) {
      newSelectedRows.push(row);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }
    setSelectedRows(newSelectedRows);
    if (newSelectedRows.length > 0) {
      props?.setFormObject?.(newSelectedRows[0]);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = Number.parseInt(event.target.value, 10);
    setPaginationSettings((prev) => ({
      ...prev,
      rowsPerPage: newRowsPerPage,
    }));
    setPage(1); // Reset to first page
  };

  // Handle pagination settings update
  const handlePaginationSettingsUpdate = (newSettings) => {
    setPaginationSettings(newSettings);
    setPage(1); // Reset to first page when settings change
  };

  // Filter visible columns based on visibilityStatus

  const onSearch = (searchValue) => {
    const filteredData = data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchValue.toLowerCase())
      )
    );
    setTableData(filteredData);
  };

  const handleSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  // Sorted data
  const sortedData = useMemo(() => {
    return stableSort(data || [], getComparator(order, orderBy));
  }, [data, order, orderBy]);

  useEffect(() => {
    // Update tableData whenever sortedData or pagination changes
    const paginatedData = sortedData?.slice(
      (page - 1) * paginationSettings.rowsPerPage,
      page * paginationSettings.rowsPerPage
    );
    setTableData(paginatedData);
  }, [data, sortedData, page, paginationSettings.rowsPerPage]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        gap: header && 2,
      }}
    >
      <Header
        Icon={Icon}
        title={title}
        showAddButton={showAddButton}
        addButtonText={addButtonText}
        onAddClick={onAddClick ? onAddClick : handleAddClick}
        showActions={showActions}
        actionOptions={actionOptions}
        selectedAction={selectedAction}
        onDeleteClick={onDeleteClick}
        handleFilterClick={handleFilterClick}
        handleFilterClose={handleFilterClose}
        openFilter={openFilter}
        anchorElFilter={anchorElFilter}
        onRefresh={onRefresh}
        columns={columns}
        openOptions={openOptions}
        anchorElOptions={anchorElOptions}
        handleOptionsClick={handleOptionsClick}
        handleOptionsClose={handleOptionsClose}
        handleAttributesClick={handleAttributesClick}
        handleSettingsClick={handleSettingsClick}
        openAttributes={openAttributes}
        anchorElAttributes={anchorElAttributes}
        handleAttributesClose={handleAttributesClose}
        handleLayoutClick={handleLayoutClick}
        handleLayoutClose={handleLayoutClose}
        layoutView={layoutView}
        onLayoutChange={onLayoutChange}
        openLayout={openLayout}
        anchorElLayout={anchorElLayout}
        visibilityStatus={visibilityStatus}
        handleToggle={handleToggle}
        selectedRows={selectedRows}
        onModifyClick={handleModifyClick}
        object={object}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.tertiary.main,
          borderRadius: 2,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            backgroundColor: "transparent",
            padding: "18px 12px",
            boxSizing: "border-box",
          }}
        >
          <SearchBarContainer onSearch={onSearch} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Rows per page selector */}
            {paginationSettings.showRowsPerPageSelector && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.color }}
                >
                  Rows per page:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={paginationSettings.rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    sx={{
                      color: theme.palette.text.color,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.border.color,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                      "& .MuiSvgIcon-root": {
                        color: theme.palette.text.color,
                      },
                      minWidth: 60,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: theme.palette.secondary.main,
                          color: theme.palette.text.color,
                        },
                      },
                    }}
                  >
                    {[5, 10, 15, 20, 25, 50, 100].map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{
                          color: theme.palette.text.color,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main + "20",
                          },
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Page info */}
            {paginationSettings.showPageInfo && (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.color }}
              >
                Page {page} of {totalPages}
              </Typography>
            )}

            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              showFirstButton={paginationSettings.showFirstLast}
              showLastButton={paginationSettings.showFirstLast}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: theme.palette.text.color,
                  border: "none",
                  minWidth: 24,
                  height: 24,
                  fontSize: "0.75rem",
                  borderRadius: 0,
                },
                "& .Mui-selected": {
                  backgroundColor: "transparent !important",
                  border: "none",
                  borderBottom: "1px solid #006BCE",
                  color: "#006BCE",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `1px solid ${theme.palette.border.color}`,
                  borderRadius: "50% !important",
                },
              }}
            />

            {/* Settings icon for pagination */}
            <Tooltip title="Pagination Settings">
              <IconButton
                onClick={() => setSettingsModalOpen(true)}
                sx={{
                  color: theme.palette.text.color,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main + "20",
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer
          sx={{
            width: "-webkit-fill-available",
            boxSizing: "border-box",
            maxHeight: "100%",
            overflow: "auto",
            color: theme.palette.text.primary,
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: 'transparent',
              borderRadius: "3px",
            },
            padding: 1,
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{
              // padding: 2,
              paddingTop: 0,
              boxSizing: "border-box",
              width: '100%',
              border: `1px solid ${theme.palette.border.color}`
            }}
          >
            {/* Table Head */}
            <TableHead key="2">
              <TableRow>
                <TableCell
                  align="center"
                  padding="checkbox"
                  className={classes.headerCell}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.primary
                  }}
                >
                  <Checkbox
                    color="primary"
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={handleSelectAllClick}
                    sx={{
                      color: "#f5f5f5",
                      "& .MuiSvgIcon-root": {
                        fontSize: "14px !important",
                      },
                      "&.Mui-checked": { color: "#006bce" },
                      "&.MuiCheckbox-indeterminate": { color: "#006bce" },
                    }}
                  />
                </TableCell>
                {columns.map((column) => (
                  <>
                    {/* {column.display && ( */}
                    <TableCell
                      key={column.id}
                      align="center"
                      className={classes.headerCell}
                      sx={{
                        padding: "12px 4px",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.text.primary
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() => handleSort(column.id)}
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: "14px",
                          
                          "&.Mui-active": {
                            color: theme.palette.text.primary,
                          },
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                    {/* )} */}
                  </>
                ))}
              </TableRow>
            </TableHead>
            {/* Table Body */}
            <TableBody>
              {tableData?.map((row, rowIndex) => {
                const isItemSelected = isSelected(
                  row,
                  rowIndex,
                  page,
                  paginationSettings.rowsPerPage
                );
                const labelId = `enhanced-table-checkbox-${rowIndex}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    // <--- CORRECTED: Use row.id directly for key
                    key={row.id} 
                    sx={{
                      "&:last-child td, &:last-child th": {
                        border: 0,
                      },
                      color: theme.palette.text.primary,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      "&:active": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <TableCell
                      align="center"
                      padding="checkbox"
                      className={classes.tableCell}
                      sx={{
                    color: theme.palette.text.primary
                      }}
                    >
                      <Checkbox
                        sx={{
                          color: theme.palette.text.color,
                          "&.Mui-checked": { color: "#006bce" },
                          "& .MuiSvgIcon-root": {
                            fontSize: "14px !important",
                          },
                        }}
                        checked={isItemSelected}
                        onClick={(event) =>
                          handleSelected(event, row, rowIndex)
                        }
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </TableCell>
                    {columns.map((column) => (
                      <>
                        {/* {column.display && ( */}
                        <TableCell
                          key={column.id}
                          align='center'
                          // className={classes.tableCell}
                          sx={{
                            color: theme.palette.text.primary,
                            cursor: column.onClick ? "pointer" : "default",
                          }}
                          onClick={() => column.onClick && column.onClick(row)}
                        >
                          {RenderColumnItem(
                            {
                              ...column,
                              value: row[column.id],
                              coordinates: row?.imageGeoHash?.coordinates,
                            },
                            row,
                            object,
                          ) || row[column.id]}
                        </TableCell>
                        {/* )} */}
                      </>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination Settings Modal */}
      <PaginationSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        currentSettings={paginationSettings}
        onSettingsUpdate={handlePaginationSettingsUpdate}
      />

      {/* <DeleteModal 
        open={}
        handleDelete={}
        handleClose={}
        item={}
      /> */}
    </Box>
  );
};

export default ListComponent;